import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { Phone } from '@phosphor-icons/react';
import { Avatar } from './Avatar';
import { t } from '../../i18n';

/**
 * L'appel en cours, pendant qu'on se promene ailleurs.
 *
 * Un bandeau en haut disait la meme chose, mais il mangeait la place de
 * l'en-tete de chaque ecran et se posait toujours au meme endroit — donc
 * fatalement sur ce qu'on voulait lire. Un bloc qu'on deplace regle les
 * deux : il se range dans le coin qui gene le moins, et c'est la personne
 * qui decide, pas nous.
 *
 * Il ne s'arrete qu'aux quatre coins. Laisser un objet flotter n'importe
 * ou oblige a le viser ; quatre positions se retrouvent sans regarder, et
 * lache a mi-chemin il rejoint le plus proche tout seul.
 */

type Coin = 'haut-gauche' | 'haut-droit' | 'bas-gauche' | 'bas-droit';

/** En deca, le doigt n'a pas deplace : il a appuye. */
const GLISSE_MIN_PX = 6;

export function BulleAppel({
  nom,
  avatarUrl,
  etat,
  onOuvrir,
}: {
  nom: string;
  avatarUrl: string | null;
  /** La duree, ou « Appel… » tant que la voix n'est pas passee. */
  etat: string;
  onOuvrir: () => void;
}) {
  const [coin, setCoin] = useState<Coin>('haut-droit');
  const noeud = useRef<HTMLDivElement>(null);
  const prise = useRef<{ x: number; y: number; dx: number; dy: number; bouge: boolean } | null>(
    null,
  );
  /** La place occupee au lacher : le point de depart du rattrapage. */
  const depuis = useRef<DOMRect | null>(null);
  /** L'image demandee et pas encore rendue : une seule a la fois. */
  const image = useRef(0);

  /*
    Le rattrapage se joue a la main.

    Changer de coin change `top` et `left`, qu'aucune transition ne sait
    animer depuis l'endroit ou le doigt a lache. On mesure donc la place
    d'avant et celle d'apres, et l'on rejoue le trajet entre les deux —
    le meme procede que pour l'ouverture d'une photo.
  */
  useLayoutEffect(() => {
    const n = noeud.current;
    const avant = depuis.current;
    depuis.current = null;
    if (!n || !avant) return;

    const apres = n.getBoundingClientRect();
    const dx = avant.left - apres.left;
    const dy = avant.top - apres.top;
    if (!dx && !dy) return;

    n.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
      { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }, [coin]);

  function onDown(event: PointerEvent<HTMLDivElement>) {
    const n = noeud.current;
    if (!n) return;
    /*
      Sans capture, le doigt qui sort du bloc — ce qui arrive des le
      premier centimetre — cesserait d'etre suivi et le bloc resterait
      colle a mi-chemin.
    */
    try {
      n.setPointerCapture(event.pointerId);
    } catch {
      /* Une souris de banc d'essai n'a pas toujours de capture. */
    }
    prise.current = { x: event.clientX, y: event.clientY, dx: 0, dy: 0, bouge: false };
  }

  function onMove(event: PointerEvent<HTMLDivElement>) {
    const n = noeud.current;
    const p = prise.current;
    if (!n || !p) return;

    p.dx = event.clientX - p.x;
    p.dy = event.clientY - p.y;
    if (Math.abs(p.dx) > GLISSE_MIN_PX || Math.abs(p.dy) > GLISSE_MIN_PX) p.bouge = true;

    /*
      Une seule ecriture par image.

      Le doigt emet plus d'evenements que l'ecran n'affiche d'images —
      souvent deux ou trois pour une seule. Poser la `transform` a chaque
      fois faisait donc du travail jete, et le geste accrochait. On retient
      la derniere position et on ne l'ecrit qu'au moment ou le navigateur
      s'apprete a dessiner ; c'est aussi ce qui la synchronise avec
      l'image, au lieu de tomber au milieu.

      Le rendu React reste hors du chemin, comme avant : rien ici ne
      change d'etat.
    */
    if (image.current) return;
    image.current = requestAnimationFrame(() => {
      image.current = 0;
      const encore = prise.current;
      if (!encore) return;
      n.style.transform = `translate3d(${encore.dx}px, ${encore.dy}px, 0)`;
    });
  }

  function onUp() {
    const n = noeud.current;
    const p = prise.current;
    prise.current = null;
    if (image.current) {
      cancelAnimationFrame(image.current);
      image.current = 0;
    }
    if (!n || !p) return;

    // Un appui sans deplacement rouvre l'appel : c'est le geste attendu.
    if (!p.bouge) {
      n.style.transform = '';
      return onOuvrir();
    }

    // Le coin le plus proche du centre du bloc, quelle que soit la
    // distance parcourue : lache a mi-chemin, il finit le voyage.
    const place = n.getBoundingClientRect();
    const cx = place.left + place.width / 2;
    const cy = place.top + place.height / 2;
    const vers: Coin = `${cy < window.innerHeight / 2 ? 'haut' : 'bas'}-${
      cx < window.innerWidth / 2 ? 'gauche' : 'droit'
    }` as Coin;

    depuis.current = place;
    n.style.transform = '';
    setCoin(vers);
  }

  return (
    <div
      ref={noeud}
      className="call-bulle"
      data-coin={coin}
      role="button"
      tabIndex={0}
      aria-label={t.composants.bulleAppel.revenir(nom)}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onKeyDown={(event) => event.key === 'Enter' && onOuvrir()}
    >
      {/* En ligne, forcement : on lui parle. */}
      <Avatar user={{ username: nom, avatarUrl, online: true }} size={44} />
      <p className="call-bulle__nom">{nom}</p>
      <p className="call-bulle__etat">
        <Phone size={12} weight="fill" />
        {etat}
      </p>
    </div>
  );
}
